<template>
  <div>
    <RctNavbar :links="navLinks" />

    <!-- HERO -->
    <section class="hero">
      <div class="hero-content">
        <p class="hero-eyebrow">Software Consulting · Athens, AL</p>
        <h1 class="hero-headline">Code built to<br><em>perform under pressure.</em></h1>
        <p class="hero-sub">RidgeCodeTech delivers high-quality software consulting for teams and businesses that need reliable, well-engineered solutions — not boilerplate.</p>
        <div class="hero-actions">
          <RctButton href="#contact">Work With Us</RctButton>
          <RctButton variant="ghost" href="#services">See what we build →</RctButton>
        </div>
      </div>
      <div class="ridge-divider">
        <svg viewBox="0 0 1130 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="80" width="1130" height="40" fill="#f5f8ff"/>
          <path d="M0,120 L0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92 L1130,120 Z" fill="#f5f8ff"/>
          <path d="M0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92" fill="none" stroke="#becdea" stroke-width="1.5" opacity="0.6"/>
        </svg>
      </div>
    </section>

    <!-- SERVICES -->
    <section class="services" id="services">
      <div class="section-inner">
        <p class="section-label reveal">What We Build</p>
        <h2 class="section-heading reveal reveal-delay-1">Engineering that fits the problem,<br>not the other way around.</h2>
        <div class="services-grid">
          <a class="service-card reveal reveal-delay-1" href="https://custom.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="6" width="32" height="22" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M12 18l4 4 8-8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">Custom Software Development</h3>
            <p class="service-desc">Web applications, APIs, and backend systems built to your requirements. Full-stack capability from database to UI — no off-the-shelf shortcuts.</p>
          </a>
          <a class="service-card reveal reveal-delay-2" href="https://ai.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="18" r="6" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M18 4v4M18 28v4M4 18h4M28 18h4" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M8.2 8.2l2.8 2.8M25 25l2.8 2.8M8.2 27.8l2.8-2.8M25 11l2.8-2.8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <h3 class="service-title">Systems &amp; Performance Engineering</h3>
            <p class="service-desc">High-throughput backends and performance-critical components in Rust. Built for reliability, speed, and memory safety — especially where it matters most.</p>
          </a>
          <a class="service-card reveal reveal-delay-3" href="https://consulting.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 10h24M6 18h16M6 26h20" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <h3 class="service-title">Technical Consulting</h3>
            <p class="service-desc">Architecture reviews, technology decisions, and engineering strategy for startups and small teams. Clear advice without the agency overhead.</p>
          </a>
          <a class="service-card reveal reveal-delay-4" href="https://web.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <rect x="20" y="4" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <rect x="4" y="20" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <rect x="20" y="20" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
            </svg>
            <h3 class="service-title">Full-Stack Web Applications</h3>
            <p class="service-desc">React, Vue, and Node.js applications for small businesses, startups, and internal tools. From MVP to production-ready, end to end.</p>
          </a>
          <a class="service-card reveal reveal-delay-1" href="https://intern.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="12" r="5" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M8 30c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M13 21l5 4 5-4" stroke="#7a96c1" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">Student Internship Program</h3>
            <p class="service-desc">Real engineering experience for CS and SE students — working on actual client projects and open source, with portfolio-building built in.</p>
          </a>
        </div>
      </div>
    </section>

    <!-- STACK -->
    <section class="stack-section" id="stack">
      <div class="section-inner">
        <p class="section-label reveal">Technology Stack</p>
        <h2 class="section-heading reveal reveal-delay-1">The right tool for the job.<br>Every time.</h2>
        <p class="section-body reveal reveal-delay-2">We lead with Rust for performance-critical systems work, and reach for the best fit across the full stack — not just the most familiar tool.</p>
        <div class="stack-pills reveal reveal-delay-3">
          <span class="pill primary">Rust</span>
          <span class="pill">Python</span>
          <span class="pill">Java</span>
          <span class="pill">JavaScript</span>
          <span class="pill">TypeScript</span>
          <span class="pill">React.js</span>
          <span class="pill">Vue.js</span>
          <span class="pill">Node.js</span>
          <span class="pill">Swift</span>
          <span class="pill">PostgreSQL</span>
          <span class="pill">MySQL</span>
          <span class="pill">MongoDB</span>
        </div>
      </div>
    </section>

    <!-- ABOUT -->
    <section class="about" id="about">
      <div class="section-inner">
        <p class="section-label reveal">About</p>
        <h2 class="section-heading reveal reveal-delay-1">Engineering with context.</h2>
        <div class="about-layout">
          <div class="about-text reveal reveal-delay-2">
            <p>RidgeCodeTech LLC is an independent software consultancy based in Athens, Alabama. We work with small businesses, startups, and development teams who need focused engineering help — without the overhead of a large agency.</p>
            <p>Our background is in <strong>high-responsibility, high-stakes environments</strong>. Before software, that meant military service, crisis work, and years of leading people in demanding conditions. We bring that same standard of reliability to every engagement.</p>
            <p>We're not a staff augmentation shop. We're engineers who think about the problem, communicate clearly, and deliver work that holds up.</p>
          </div>
          <div class="about-stats reveal reveal-delay-3">
            <div class="stat-box">
              <div class="stat-number">Full</div>
              <div class="stat-label">Stack capability, front to back</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">MS</div>
              <div class="stat-label">Computer Science in progress</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">Rust</div>
              <div class="stat-label">Primary language for systems work</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">AL</div>
              <div class="stat-label">Based in Athens, work anywhere</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT -->
    <section class="contact-section" id="contact">
      <div class="section-inner">
        <p class="section-label reveal">Get In Touch</p>
        <h2 class="section-heading reveal reveal-delay-1">Have a project in mind?</h2>
        <div class="contact-layout">
          <div class="contact-aside reveal reveal-delay-2">
            <p>We take on a limited number of consulting engagements at a time. Tell us what you're building and we'll get back to you within one business day.</p>
            <div class="contact-detail">
              <a href="mailto:hello@ridgecodetech.com">hello@ridgecodetech.com</a>
              <a href="https://consulting.ridgecodetech.com">consulting.ridgecodetech.com</a>
            </div>
          </div>
          <div class="reveal reveal-delay-3">
            <form class="contact-form" novalidate @submit.prevent="handleSubmit">
              <div class="form-row">
                <div class="form-group">
                  <label for="name">Name</label>
                  <input v-model="formName" type="text" id="name" name="name" placeholder="Your name" required />
                </div>
                <div class="form-group">
                  <label for="email">Email</label>
                  <input v-model="formEmail" type="email" id="email" name="email" placeholder="you@company.com" required />
                </div>
              </div>
              <div class="form-group">
                <label for="service">Service</label>
                <select v-model="formService" id="service" name="service" required>
                  <option value="" disabled>What do you need help with?</option>
                  <option value="Custom Software Development">Custom Software Development</option>
                  <option value="Systems & Performance Engineering">Systems &amp; Performance Engineering (Rust)</option>
                  <option value="Technical Consulting">Technical Consulting</option>
                  <option value="Full-Stack Web Application">Full-Stack Web Application</option>
                  <option value="Other">Other / Not sure yet</option>
                </select>
              </div>
              <div class="form-group">
                <label for="message">Message</label>
                <textarea v-model="formMessage" id="message" name="message" placeholder="Tell us about your project, timeline, and any constraints..." required></textarea>
              </div>
              <div v-if="formStatus.msg" :class="['form-status', formStatus.type]">{{ formStatus.msg }}</div>
              <RctButton type="submit" :loading="formLoading" :disabled="formLoading">Send Message</RctButton>
            </form>
          </div>
        </div>
      </div>
    </section>

    <AgentChat scope="main" :agent-url="AGENT_URL" />

    <RctFooter :links="footerLinks" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { RctNavbar, RctButton, RctFooter, AgentChat } from '@rct/ui';
import { NAV_LINKS, FOOTER_LINKS, WORKER_URL, AGENT_URL, isValidEmail } from '@rct/shared';

const navLinks = [
  ...NAV_LINKS,
  { href: 'https://web.ridgecodetech.com/sign-in', label: 'Sign In' },
] as { href: string; label: string }[];
const footerLinks = [...FOOTER_LINKS] as { href: string; label: string }[];

const formName    = ref('');
const formEmail   = ref('');
const formService = ref('');
const formMessage = ref('');
const formLoading = ref(false);
const formStatus  = ref<{ type: 'success' | 'error' | ''; msg: string }>({ type: '', msg: '' });

async function handleSubmit() {
  const name    = formName.value.trim();
  const email   = formEmail.value.trim();
  const service = formService.value;
  const message = formMessage.value.trim();

  if (!name || !email || !service || !message) {
    formStatus.value = { type: 'error', msg: 'Please fill in all fields before sending.' };
    return;
  }
  if (!isValidEmail(email)) {
    formStatus.value = { type: 'error', msg: 'Please enter a valid email address.' };
    return;
  }

  formLoading.value = true;
  formStatus.value  = { type: '', msg: '' };

  try {
    const res = await fetch(WORKER_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, email, service, message }),
    });

    if (res.ok) {
      formStatus.value = { type: 'success', msg: "Message sent — we'll be in touch within one business day." };
      formName.value = formEmail.value = formService.value = formMessage.value = '';
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      formStatus.value = { type: 'error', msg: data.error ?? 'Something went wrong. Please try emailing us directly.' };
    }
  } catch {
    formStatus.value = { type: 'error', msg: 'Network error — please try emailing us directly at hello@ridgecodetech.com' };
  } finally {
    formLoading.value = false;
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
});
</script>

<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --navy:       #0f264f;
  --navy-deep:  #091a38;
  --navy-mid:   #1a3a6e;
  --periwinkle: #becdea;
  --steel:      #7a96c1;
  --pale:       #e1ecff;
  --off-white:  #f5f8ff;
  --white:      #ffffff;
  --text-body:  #2c3e5c;
  --text-muted: #6b82a0;
}

html { scroll-behavior: smooth; }

body {
  font-family: 'Inter', sans-serif;
  background: var(--off-white);
  color: var(--text-body);
  overflow-x: hidden;
}

/* SCROLL REVEAL */
.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.65s cubic-bezier(.4,0,.2,1), transform 0.65s cubic-bezier(.4,0,.2,1);
}
.reveal.visible { opacity: 1; transform: none; }
.reveal-delay-1 { transition-delay: 0.1s; }
.reveal-delay-2 { transition-delay: 0.2s; }
.reveal-delay-3 { transition-delay: 0.3s; }
.reveal-delay-4 { transition-delay: 0.4s; }

/* HERO */
.hero {
  min-height: 100vh;
  background: var(--navy-deep);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10rem 4rem 0;
  position: relative;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background:
    radial-gradient(ellipse 60% 50% at 70% 30%, rgba(122,150,193,0.12) 0%, transparent 70%),
    radial-gradient(ellipse 40% 40% at 20% 70%, rgba(15,38,79,0.8) 0%, transparent 60%);
  pointer-events: none;
}
.hero-content { position: relative; max-width: 820px; }
.hero-eyebrow {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--steel);
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.2s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-headline {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 700;
  line-height: 1.08;
  color: var(--white);
  letter-spacing: -0.02em;
  margin-bottom: 1.75rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.4s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-headline em { font-style: normal; color: var(--periwinkle); }
.hero-sub {
  font-size: 1.15rem;
  font-weight: 300;
  color: var(--periwinkle);
  line-height: 1.7;
  max-width: 540px;
  margin-bottom: 3rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.6s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  opacity: 0;
  animation: fadeUp 0.7s 0.75s cubic-bezier(.4,0,.2,1) forwards;
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: none; }
}

/* RIDGE DIVIDER */
.ridge-divider {
  position: relative;
  width: 100%;
  height: 120px;
  background: var(--navy-deep);
  margin-top: 6rem;
}
.ridge-divider svg { position: absolute; bottom: 0; left: 0; width: 100%; height: 100%; }

/* SECTION BASE */
section { padding: 6rem 4rem; }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--steel);
  margin-bottom: 1rem;
}
.section-heading {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--navy);
  line-height: 1.2;
  margin-bottom: 1.5rem;
}
.section-body {
  font-size: 1rem;
  font-weight: 300;
  color: var(--text-muted);
  line-height: 1.8;
  max-width: 600px;
}

/* SERVICES */
.services { background: var(--off-white); }
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2px;
  margin-top: 3.5rem;
  border: 2px solid var(--periwinkle);
}
.service-card {
  display: block;
  padding: 2.5rem 2rem;
  background: var(--white);
  border-right: 2px solid var(--periwinkle);
  transition: background 0.2s;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}
.service-card:last-child { border-right: none; }
.service-card:hover { background: var(--pale); }
.service-icon { width: 36px; height: 36px; margin-bottom: 1.25rem; opacity: 0.7; }
.service-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--navy);
  margin-bottom: 0.75rem;
}
.service-desc { font-size: 0.875rem; line-height: 1.7; color: var(--text-muted); font-weight: 300; }

/* STACK */
.stack-section { background: var(--navy); color: var(--pale); }
.stack-section .section-heading { color: var(--pale); }
.stack-section .section-body { color: var(--periwinkle); }
.stack-pills { display: flex; flex-wrap: wrap; gap: 0.625rem; margin-top: 2.5rem; }
.pill {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  padding: 0.5rem 1rem;
  border: 1px solid var(--navy-mid);
  color: var(--periwinkle);
  border-radius: 2px;
  transition: border-color 0.2s, color 0.2s, transform 0.15s;
  cursor: default;
}
.pill:hover { border-color: var(--steel); color: var(--pale); transform: translateY(-2px); }
.pill.primary { border-color: var(--steel); color: var(--white); background: rgba(122,150,193,0.12); }

/* ABOUT */
.about { background: var(--off-white); }
.about-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
  margin-top: 3rem;
}
.about-text p { font-size: 1rem; line-height: 1.85; color: var(--text-muted); font-weight: 300; margin-bottom: 1.25rem; }
.about-text p:last-child { margin-bottom: 0; }
.about-text strong { color: var(--navy); font-weight: 500; }
.about-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5px;
  background: var(--periwinkle);
  border: 1.5px solid var(--periwinkle);
}
.stat-box { background: var(--white); padding: 2rem 1.75rem; transition: background 0.2s; }
.stat-box:hover { background: var(--pale); }
.stat-number {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2.25rem;
  font-weight: 700;
  color: var(--navy);
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 0.5rem;
}
.stat-label { font-size: 0.8rem; color: var(--text-muted); font-weight: 400; line-height: 1.4; }

/* CONTACT */
.contact-section { background: var(--navy-deep); padding: 7rem 4rem; }
.contact-section .section-label { color: var(--steel); }
.contact-section .section-heading { color: var(--pale); }
.contact-layout {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 5rem;
  align-items: start;
  margin-top: 3rem;
}
.contact-aside p {
  font-size: 0.9rem;
  color: var(--periwinkle);
  line-height: 1.8;
  font-weight: 300;
  margin-bottom: 2rem;
}
.contact-detail { display: flex; flex-direction: column; gap: 0.75rem; }
.contact-detail a {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.875rem;
  color: var(--steel);
  text-decoration: none;
  border-bottom: 1px solid var(--navy-mid);
  padding-bottom: 2px;
  width: fit-content;
  transition: color 0.2s, border-color 0.2s;
}
.contact-detail a:hover { color: var(--pale); border-color: var(--steel); }
.contact-form { display: flex; flex-direction: column; gap: 1.25rem; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
.form-group { display: flex; flex-direction: column; gap: 0.4rem; }
.form-group label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--steel);
}
.form-group input,
.form-group select,
.form-group textarea {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 300;
  color: var(--pale);
  background: rgba(255,255,255,0.05);
  border: 1px solid var(--navy-mid);
  border-radius: 3px;
  padding: 0.8rem 1rem;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
  width: 100%;
}
.form-group select option { background: var(--navy-deep); color: var(--pale); }
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus { border-color: var(--steel); background: rgba(255,255,255,0.08); }
.form-group textarea { resize: vertical; min-height: 130px; }
.form-group input::placeholder,
.form-group textarea::placeholder { color: var(--text-muted); }
.form-status {
  font-size: 0.875rem;
  padding: 0.75rem 1rem;
  border-radius: 3px;
}
.form-status.success { background: rgba(122,150,193,0.15); border: 1px solid var(--steel); color: var(--pale); }
.form-status.error   { background: rgba(180,60,60,0.15);  border: 1px solid #b44; color: #f5a0a0; }

/* RESPONSIVE */
@media (max-width: 900px) {
  .contact-layout { grid-template-columns: 1fr; gap: 3rem; }
  .about-layout   { grid-template-columns: 1fr; gap: 3rem; }
}
@media (max-width: 768px) {
  section { padding: 4rem 1.5rem; }
  .hero { padding: 7rem 1.5rem 0; min-height: 90svh; }
  .hero-headline { font-size: clamp(2rem, 9vw, 3.5rem); }
  .hero-sub { font-size: 1rem; }
  .services-grid { grid-template-columns: 1fr; }
  .service-card { border-right: none; border-bottom: 2px solid var(--periwinkle); }
  .service-card:last-child { border-bottom: none; }
  .form-row { grid-template-columns: 1fr; }
  .contact-section { padding: 4rem 1.5rem; }
  .about-stats { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 480px) {
  .about-stats { grid-template-columns: 1fr; }
  .stat-box { padding: 1.5rem 1.25rem; }
}
@media (prefers-reduced-motion: reduce) {
  *, .hero-eyebrow, .hero-headline, .hero-sub, .hero-actions {
    animation: none !important;
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
  .reveal { opacity: 1; transform: none; }
}
</style>
